import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Grid, Paper, Stack, Typography } from '@esgian/esgianui';
import VesselAvgSpeedChart from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselActivitySection/VesselAvgSpeedChart';
import VesselActivityProfileChart from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselActivitySection/VesselActivityProfileChart';
import { TextWithTooltipIcon } from '@components';
import moment from 'moment';
import { getVesselAvgMonthlySpeed, getVesselMonthlyPercentActivityStatus } from '@api';
import { useParams } from 'react-router-dom';
import { DateRangePicker } from '../../../../../Inputs';
import { useQuery } from '@tanstack/react-query';
import { VesselPortCallsTable } from '@components/Tables';
import { useSegment } from '@hooks/useSegment';
import {
  VESSEL_ACTIVITY_SPEED_RORO_TITLE,
  VESSEL_ACTIVITY_SPEED_MPP_TITLE
} from '@constants/tooltips';
import { GEOGRAPHICAL_TYPES } from '@constants';

let defaultPeriod = {
  start: moment().subtract(1, 'year').startOf('month'),
  end: moment()
};

function VesselActivitySection({ portCallsQuery = {}, vesselName = '' }) {
  const { isRoRo } = useSegment();
  const params = useParams();
  const [activityDates, setActivityDates] = useState({
    ...defaultPeriod
  });
  const [speedDates, setSpeedDates] = useState({
    ...defaultPeriod
  });

  const vesselActivityQuery = useQuery({
    queryKey: ['vesselActivity', params.imo, activityDates.start, activityDates.end],
    enabled: !!params.imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselMonthlyPercentActivityStatus(signal, queryKey[1], queryKey[2], queryKey[3])
        .then((result) => result)
        .catch(() => null);
    }
  });
  const vesselAvgSpeedQuery = useQuery({
    queryKey: ['vesselSpeed', params.imo, speedDates.start, speedDates.end],
    enabled: !!params.imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselAvgMonthlySpeed(queryKey[1], queryKey[2], queryKey[3], 2.0, signal)
        .then((result) => result)
        .catch(() => null);
    }
  });

  return (
    <>
      <Grid item xs={6}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Stack spacing={2}>
            <Stack>
              <TextWithTooltipIcon
                label={'Average speed'}
                tooltipText={
                  isRoRo ? VESSEL_ACTIVITY_SPEED_RORO_TITLE : VESSEL_ACTIVITY_SPEED_MPP_TITLE
                }
                labelVariant={'h6'}
              />
              <Box sx={{ width: '10em' }}>
                <DateRangePicker
                  inputVariant={'standard'}
                  startDate={speedDates.start}
                  endDate={speedDates.end}
                  onClose={(start, end) => {
                    setSpeedDates({
                      start: start,
                      end: end.isSame(moment(), 'month') ? moment() : end
                    });
                  }}
                />
              </Box>
            </Stack>
            <Divider />
            <VesselAvgSpeedChart
              vesselAvgSpeedData={vesselAvgSpeedQuery.data}
              loading={vesselAvgSpeedQuery.isFetching}
              fileName={vesselName?.split(' ')?.join('-').toLowerCase() + '-average-speed'}
            />
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={6}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack>
              <TextWithTooltipIcon
                label={<Typography variant={'h6'}>Activity profile</Typography>}
                tooltipText={
                  <Stack>
                    <Typography variant={'caption'}>Transit: Vessel Speed &gt; 3 kn</Typography>
                    <Typography variant={'caption'}>
                      Standby: 0 kn &lt; Vessel Speed &le; 3 kn
                    </Typography>
                    <Typography variant={'caption'}>At Port: Vessel at port</Typography>
                  </Stack>
                }
              />
              <Box sx={{ width: '10em' }}>
                <DateRangePicker
                  inputVariant={'standard'}
                  startDate={activityDates.start}
                  endDate={activityDates.end}
                  onClose={(start, end) => {
                    setActivityDates({ start: start, end: end });
                  }}
                />
              </Box>
            </Stack>
            <Divider />
            <VesselActivityProfileChart
              vesselActivityData={vesselActivityQuery.data}
              loading={vesselActivityQuery.isFetching}
              fileName={vesselName?.split(' ')?.join('-').toLowerCase() + '-activity-profile'}
            />
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <VesselPortCallsTable
          geographicalType={GEOGRAPHICAL_TYPES.ALL}
          name={vesselName?.split(' ')?.join('-')}
          vesselImo={parseInt(params.imo)}
          hideColumns={['vesselName', 'imo', 'operatorName']}
          portCalls={portCallsQuery.data}
          loading={portCallsQuery.isFetching}
          title={'Last Port Calls'}
          labels={['Vessel', 'Port', 'UNLOCODE', 'Arrival', 'Departure', 'Port Time (hours)']}
          headers={['vesselName', 'portName', 'portCode', 'startDate', 'endDate', 'timeInPort']}
        />
      </Grid>
    </>
  );
}

VesselActivitySection.propTypes = {
  portCallsQuery: PropTypes.object,
  vesselName: PropTypes.string
};

export default VesselActivitySection;
